import { useState } from "react";
import { motion } from "framer-motion";
import {
  Crown,
  MonitorSmartphone,
  MessagesSquare,
  Megaphone,
  Presentation,
  PenTool,
  Search,
  Sparkles,
  Users,
  ArrowUpRight,
} from "lucide-react";
import { Reveal } from "@/components/motion";

const SERVICES = [
  { icon: Crown, title: "Brand Strategy & Management", desc: "Positioning, identity systems and governance." },
  { icon: MonitorSmartphone, title: "Website Development & Analytics", desc: "Design, build and measure digital experiences." },
  { icon: MessagesSquare, title: "Social Media Management", desc: "Content, community and analytics that convert." },
  { icon: Megaphone, title: "Digital Advertising & Campaigns", desc: "Performance campaigns with measurable ROI." },
  { icon: Presentation, title: "Outdoor Advertising", desc: "High-impact presence in the physical world." },
  { icon: PenTool, title: "Content & Creative Design", desc: "Editorial storytelling and art direction." },
  { icon: Search, title: "Search Engine Optimisation", desc: "Rank higher, get found, stay found." },
  { icon: Sparkles, title: "Answer Engine Optimisation", desc: "Optimised for the AI-answer era." },
  { icon: Users, title: "Public Relations & Comms", desc: "Reputation, narrative and media relations." },
];

export const Services = () => {
  const [hover, setHover] = useState(null);
  return (
    <section id="services" className="relative py-28 md:py-40 bg-[#F3EFE6]" data-testid="services">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
              — Our Core Services
            </p>
            <h2 className="font-serif font-light text-5xl md:text-6xl tracking-tight leading-none">
              Everything a brand
              <br />
              needs to be <em className="italic text-[#C5A059]">believed.</em>
            </h2>
          </div>
          <p className="max-w-xs text-sm text-[#78716C] leading-relaxed">
            Nine disciplines, one obsession — building brands the world trusts.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#E8E4D9]">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal
                key={s.title}
                i={i}
                className="border-b border-r border-[#E8E4D9]"
              >
                <div
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`service-${i}`}
                  className="relative group p-8 md:p-10 h-full min-h-[220px] flex flex-col justify-between overflow-hidden cursor-default"
                >
                  <motion.div
                    className="absolute inset-0 bg-[#1A1713]"
                    initial={false}
                    animate={{ opacity: hover === i ? 1 : 0 }}
                    transition={{ duration: 0.4 }}
                  />
                  <div className="relative z-10 flex items-start justify-between">
                    <Icon
                      strokeWidth={1}
                      size={38}
                      className={`transition-colors duration-400 ${
                        hover === i ? "text-[#C5A059]" : "text-[#1A1713]"
                      }`}
                    />
                    <ArrowUpRight
                      size={20}
                      className={`transition-all duration-400 ${
                        hover === i
                          ? "text-[#C5A059] opacity-100 translate-x-0 -translate-y-0"
                          : "opacity-0 translate-x-2 translate-y-2"
                      }`}
                    />
                  </div>
                  <div className="relative z-10">
                    <h3
                      className={`font-serif text-2xl md:text-[1.7rem] leading-tight transition-colors duration-400 ${
                        hover === i ? "text-[#FDFBF7]" : "text-[#1A1713]"
                      }`}
                    >
                      {s.title}
                    </h3>
                    <p
                      className={`text-sm mt-3 transition-colors duration-400 ${
                        hover === i ? "text-[#FDFBF7]/60" : "text-[#78716C]"
                      }`}
                    >
                      {s.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
